/**
 * v1 expense wire → {@link ExpenseLine} domain.
 * Import only from `*.http.ts` or other API ingress modules.
 */

import {
  financialDocumentFromV1Wire,
  type BookingInvoiceFields
} from 'v5/api/booking/bookingBillingFromV1.http';
import type {
  ExpenseLine,
  ExpenseRateInterval
} from 'v5/core/expense-billing/model';
import type { CurrencyCode } from 'v5/core/money/model';

/** Legacy v1 {@link Expense} commit/estimate fields at the HTTP boundary. */
export type ExpenseWireFields = {
  id: number;
  archived?: boolean;
  slot_category_id?: number | null;
  commit_vendor?: string | null;
  commit_rate_amount?: number | null;
  commit_rate_quantity?: number | null;
  commit_rate_currency?: string | null;
  commit_rate_interval?: string | null;
  estimate_rate_amount?: number | null;
  estimate_rate_quantity?: number | null;
  estimate_rate_currency?: string | null;
  estimate_rate_interval?: string | null;
  receipts?: BookingInvoiceFields[];
};

function parseExpenseInterval(
  interval: string | null | undefined
): ExpenseRateInterval {
  if (interval === 'HOURLY') return 'hourly';
  if (interval === 'FIXED') return 'fixed';
  if (interval === 'ITEM') return 'perItem';
  return 'daily';
}

export const expenseBillingFromV1 = {
  lineFromExpense(
    expense: ExpenseWireFields,
    phase: ExpenseLine['phase']
  ): ExpenseLine {
    const amount =
      phase === 'committed'
        ? expense.commit_rate_amount
        : expense.estimate_rate_amount;
    const quantity =
      phase === 'committed'
        ? expense.commit_rate_quantity
        : expense.estimate_rate_quantity;
    const currency =
      phase === 'committed'
        ? expense.commit_rate_currency
        : expense.estimate_rate_currency;
    const interval =
      phase === 'committed'
        ? expense.commit_rate_interval
        : expense.estimate_rate_interval;

    const vendor = expense.commit_vendor?.trim();
    const lineCurrency = (currency ?? 'USD') as CurrencyCode;
    return {
      id: String(expense.id),
      phase,
      archived: Boolean(expense.archived),
      vendor: vendor ? vendor : null,
      slotCategoryId: expense.slot_category_id ?? null,
      charge: {
        currency: lineCurrency,
        amountPerUnit: amount ?? 0,
        interval: parseExpenseInterval(interval),
        quantity: quantity ?? 0
      },
      documents: (expense.receipts ?? []).map(receipt =>
        financialDocumentFromV1Wire(receipt, 'receipt', lineCurrency)
      )
    };
  }
} as const;
